import React, { useState, useEffect, Suspense, lazy } from "react";

// routes
import {
  fetchMemberEngagementGraph,
  fetchPortalCustomerActiveUsers,
  fetchPortalCustomerActiveUsersTrends,
} from "./../routes/DashboardPortalRoutes";

// redux
import { portalApiMap } from "../../../store/actions/portalAction"; 
import { connect } from "react-redux";
import { fetchEngagementData } from "../../../store/actions/analyticsActions";
// utils
import useDebouncedEffect from "./../utils/useDebouncedEffect";
// components
import Loading from "../../Loading/Loading";
import EngagmentChart from "../components/EngagmentChart";

const PortalBarChart = lazy(() => import("./../components/PortalBarChart"));
const DateRange = lazy(() => import("./../components/DateRange"));

const PortalDashboardAnalytics = ({ portalApiMap, organizationId }) => {
  // Default date range: Last 30 days
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [endDate, setEndDate] = useState(new Date());

  // Data states for dashboard panels
  const [activeUsersData, setActiveUsersData] = useState([]);
  const [activeUsersTrends, setActiveUsersTrends] = useState(null);

  // Global loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRange, setSelectedRange] = useState("Last 30 Days");
  const [loading, setLoading] = useState(false);
  const [memberEngagementData,setMemberEngagementData] = useState("")

  const debounceDelay = selectedRange === "Custom Range" ? 1200 : 0;

  // -------------------------
  // Group 1: Active Users & Trends (debounced)
  // -------------------------
  useDebouncedEffect(
    () => {
      setIsLoading(true);
      (async () => {
        try {
          const [activeUsers, trends] = await Promise.all([
            fetchPortalCustomerActiveUsers(
              portalApiMap,
              startDate.toISOString(),
              endDate.toISOString()
            ),
            fetchPortalCustomerActiveUsersTrends(
              portalApiMap,
              startDate.toISOString(),
              endDate.toISOString()
            ),
          ]);
          setActiveUsersData(Array.isArray(activeUsers) ? activeUsers : []);
          setActiveUsersTrends(trends);
        } catch (err) {
          console.error("Error fetching active users/trends:", err);
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [selectedRange, startDate, endDate, portalApiMap],
    debounceDelay
  );

 // Group 2: Member Engagement Graph (debounced)
  // -------------------------
  useDebouncedEffect(
    () => {
      setIsLoading(true);
      (async () => {
        try {
          // Here, since our route is defined in analyticsApi, we pass Date objects directly
          // so that the formatQuery function within analyticsApi works as expected.

          const graphData = await fetchEngagementData({
            start: startDate, 
            end: endDate, 
            organizationId,
          });
          console.log("Member Engagement Graph Data:", graphData);
          setMemberEngagementData(graphData);
        } catch (err) {
          console.error("Error fetching member engagement graph:", err);
        } finally {
          setIsLoading(false);
        }
      })();
    },
    // Include organizationId so that a change in organization triggers a refetch.
    [selectedRange, startDate, endDate, organizationId],
    debounceDelay
  );

  return (
    <Suspense
      fallback={
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Loading />
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Loading />
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          Error: {error.message || "Error loading data"}
        </div>
      ) : (
        <>
          {/* Dashboard Header */}
           <DateRange
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            setActiveUsersData={setActiveUsersData}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
          />
          <PortalBarChart
            startDate={startDate}
            endDate={endDate}
            activeUsersData={activeUsersData}
            trendsData={activeUsersTrends}
            error={error}
            isLoading={isLoading}
          />
          <EngagmentChart graphData={memberEngagementData}  isLoading={isLoading}/>
        </>
      )}
    </Suspense>
  );
};

const actions = { portalApiMap };

export default connect(null, actions)(PortalDashboardAnalytics);
